import { useCallback, useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";

import { useAuth } from "../../auth/AuthContext";
import { ScreenShell } from "./ScreenShell";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

type IngestStatus = {
  latest?: {
    run_id: number;
    source: string;
    finished_at: string | null;
    ok: number | null;
    rows_ingested: number | null;
    rows_skipped: number | null;
    error: string | null;
  } | null;
};

type UploadResult = {
  status?: string;
  error?: string;
  detail?: string;
  filename?: string;
  latest?: IngestStatus["latest"];
  stdout_tail?: string;
  exit_code?: number;
  stderr?: string;
  import_module?: string;
};

const MAX_BYTES = 12 * 1024 * 1024;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error("read_failed"));
    reader.readAsDataURL(file);
  });
}

export function OpsContent() {
  const { accessToken, user, hasRole } = useAuth();
  const canIngest = hasRole("owner", "gm", "sales");
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<IngestStatus | null>(null);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    if (!accessToken) return;
    try {
      const response = await fetch("/api/ops/import-5515/status", {
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: "include",
      });
      if (response.ok) {
        setStatus((await response.json()) as IngestStatus);
      }
    } catch {
      // The latest successful state stays visible during a transient outage.
    }
  }, [accessToken]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  function pickFile(next: File | null) {
    setResult(null);
    setError(null);
    if (!next) {
      setFile(null);
      return;
    }
    if (!/\.(csv|xlsx)$/i.test(next.name)) {
      setError(
        "Expected .csv or .xlsx for reports 5110, 3110, or 1110.",
      );
      setFile(null);
      return;
    }
    if (next.size > MAX_BYTES) {
      setError("File too large. Maximum size is 12 MB.");
      setFile(null);
      return;
    }
    setFile(next);
  }

  async function runIngest() {
    if (!file || !accessToken) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const fileBase64 = await fileToBase64(file);
      const response = await fetch("/api/ops/import-5515/upload", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          filename: file.name.replace(/[^A-Za-z0-9._-]/g, "_"),
          file_base64: fileBase64,
          confirm_upload: "INGEST",
        }),
      });
      const body = (await response.json().catch(() => ({}))) as UploadResult;
      if (!response.ok) {
        setError(
          body.detail ||
            body.error ||
            body.stderr ||
            `Ingest failed (HTTP ${response.status})`,
        );
        setResult(body);
        return;
      }
      setResult(body);
      setFile(null);
      await loadStatus();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Network error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <ScreenShell
      badge="Ops"
      title="Import data"
      subtitle="Import audited Vonigo report files. Vonigo API access is permanently retired."
      unframed
    >
      <div className="mb-5 flex justify-end">
        <Link
          to="/quotes"
          className="text-xs font-medium text-slate-500 underline-offset-2 hover:underline"
        >
          Back to Quotes
        </Link>
      </div>

      <div className="space-y-5">
        <Card className="border-[#11133f]/20 bg-slate-50/80">
          <CardHeader className="pb-2">
            <CardTitle>Which Vonigo file?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-700">
            <p className="text-slate-600">
              Use the same drop zone for all three exports. Keep the report
              number in the filename so Murray routes it correctly.
            </p>
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
              <table className="w-full min-w-[640px] text-left text-xs">
                <thead className="bg-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Goal</th>
                    <th className="px-3 py-2 font-semibold">Report</th>
                    <th className="px-3 py-2 font-semibold">Filename hint</th>
                    <th className="px-3 py-2 font-semibold">Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr>
                    <td className="px-3 py-2.5 font-semibold text-[#11133f]">
                      Quotes and follow-up
                    </td>
                    <td className="px-3 py-2.5">
                      <strong>5110</strong> Quotes (Nifty)
                    </td>
                    <td className="px-3 py-2.5 font-mono text-[11px]">
                      5110 or Quotes
                    </td>
                    <td className="px-3 py-2.5">
                      Quote pipeline, cadence and Today queue
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2.5 font-semibold text-[#11133f]">
                      Reviews
                    </td>
                    <td className="px-3 py-2.5">
                      <strong>3110</strong> Invoices
                    </td>
                    <td className="px-3 py-2.5 font-mono text-[11px]">
                      3110, Invoice or WFY
                    </td>
                    <td className="px-3 py-2.5">
                      Paid invoices eligible for the review workflow
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2.5 font-semibold text-[#11133f]">
                      Technician capacity
                    </td>
                    <td className="px-3 py-2.5">
                      <strong>1110</strong> Work By Tech
                    </td>
                    <td className="px-3 py-2.5 font-mono text-[11px]">
                      1110 or Work_By_Tech
                    </td>
                    <td className="px-3 py-2.5">
                      Technician routes, work days and capacity bars
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr]">
          <Card>
            <CardHeader>
              <CardTitle>Upload Vonigo export</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!canIngest ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Role {user?.role || "unknown"} cannot ingest files.
                </div>
              ) : (
                <>
                  <div
                    className={`flex min-h-[160px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-8 text-center transition ${
                      dragOver
                        ? "border-[#11133f] bg-blue-50"
                        : "border-slate-200 bg-slate-50 hover:border-slate-300"
                    }`}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(event) => {
                      event.preventDefault();
                      setDragOver(false);
                      pickFile(event.dataTransfer.files?.[0] || null);
                    }}
                    onClick={() =>
                      document.getElementById("vonigo-report-file")?.click()
                    }
                  >
                    <div className="text-sm font-medium text-[#11133f]">
                      Drop CSV or XLSX here, or click to choose
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      Maximum size: 12 MB
                    </div>
                    {file ? (
                      <div className="mt-3 rounded-md bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm">
                        {file.name} ({(file.size / 1024).toFixed(0)} KB)
                      </div>
                    ) : null}
                    <input
                      id="vonigo-report-file"
                      type="file"
                      accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      className="hidden"
                      onChange={(event) =>
                        pickFile(event.target.files?.[0] || null)
                      }
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      disabled={!file || busy}
                      onClick={() => void runIngest()}
                    >
                      {busy ? "Ingesting..." : "Run ingest"}
                    </Button>
                    {file ? (
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={busy}
                        onClick={() => pickFile(null)}
                      >
                        Cancel
                      </Button>
                    ) : null}
                  </div>
                </>
              )}

              {error ? (
                <div className="whitespace-pre-wrap rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  {error}
                </div>
              ) : null}
              {result?.status === "ok" ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-900">
                  <div className="font-semibold">Ingest OK</div>
                  <div className="mt-1 text-xs">
                    File: {result.filename}
                    {result.import_module
                      ? ` | route: ${result.import_module}`
                      : null}
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <div className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle>Last file ingest</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-slate-700">
                {status?.latest ? (
                  <>
                    <div>
                      <span className="text-slate-500">Run</span>{" "}
                      {status.latest.run_id}
                    </div>
                    <div>
                      <span className="text-slate-500">Source</span>{" "}
                      <span className="break-all font-mono text-xs">
                        {status.latest.source}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">Rows</span>{" "}
                      {status.latest.rows_ingested ?? "unknown"} ingested
                    </div>
                    <div>
                      <span className="text-slate-500">Status</span>{" "}
                      {status.latest.ok === 1 ? "OK" : "Failed"}
                    </div>
                  </>
                ) : (
                  <p className="text-slate-500">No ingest recorded yet.</p>
                )}
                <Button
                  type="button"
                  variant="secondary"
                  className="mt-2"
                  onClick={() => void loadStatus()}
                >
                  Refresh
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Vonigo access policy</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-xs text-slate-600">
                <p>API access is permanently retired.</p>
                <p>No credential storage or API synchronization is available.</p>
                <p>Only audited report files can update Murray.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </ScreenShell>
  );
}
